import React, { useState, useEffect, useCallback } from 'react';
import { FiUsers, FiMail, FiBriefcase, FiStar, FiShield, FiUser, FiEdit2, FiTrash2, FiUserMinus } from 'react-icons/fi';
import AxiosInstance from '../../AxiosInstance';
import config from '../../config/frontend.config';
import showToast from '../../components/Toast';
import { usePermissions } from '../../contexts/PermissionContext';
import './TeamMembers.css';

const TeamMembers = () => {
  const { hasPermission } = usePermissions();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, active, inactive
  const [searchTerm, setSearchTerm] = useState('');

  const userId = config.getUserId();

  // Same data fetching logic as Team.js
  const fetchTeamMembers = useCallback(async (userId) => {
    try {
      setLoading(true);
      const BASE_URL = config.api.baseURL + '/' + config.api.apiPath;
      const response = await AxiosInstance.get(`${BASE_URL}/user/getallteammember/${userId}`);
      
      // Handle new API response structure (same as Team.js pattern)
      if (response.data && response.data.statusCode === 200) {
        setTeamMembers(response.data.data || []);
      } else if (response.data && response.data.data) {
        // Fallback for old structure
        setTeamMembers(response.data.data || []);
      } else {
        setTeamMembers([]);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
      // Toast notification is handled by AxiosInstance interceptor
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (userId) {
      fetchTeamMembers(userId);
    }
  }, [fetchTeamMembers, userId]);


  const handleDeleteUser = async (userIdToDelete) => {
    // Check if user has permission to delete team members permanently
    if (!hasPermission('canDeleteTeamMembers')) {
      showToast.error('You do not have permission to delete team members permanently');
      return;
    }

    // Get member details for confirmation
    const member = teamMembers.find(m => m.UserId === userIdToDelete);
    if (!member) {
      showToast.error('Member not found');
      return;
    }

    const confirmMessage = `Are you sure you want to permanently delete ${member.FirstName} ${member.LastName}? This action cannot be undone and will remove all their data permanently.`;
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      setLoading(true);
      const BASE_URL = config.api.baseURL + '/' + config.api.apiPath;
      const response = await AxiosInstance.delete(`${BASE_URL}/user/deleteuser/${userIdToDelete}`);
      
      if (response.status === 200) {
        showToast.success(`${member.FirstName} ${member.LastName} deleted permanently`);
        fetchTeamMembers(userId); // Refresh the list
      } else {
        showToast.error('Failed to delete user permanently');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      // Toast notification is handled by AxiosInstance interceptor
    } finally {
      setLoading(false);
    }
  };

  const handleEditMember = async (userIdToEdit) => {
    // Check if user has permission to edit team members
    if (!hasPermission('canEditTeamMembers')) {
      showToast.error('You do not have permission to edit team members');
      return;
    }

    try {
      // Get member details for editing
      const member = teamMembers.find(m => m.UserId === userIdToEdit);
      if (!member) {
        showToast.error('Member not found');
        return;
      }

      // TODO: Open edit modal or navigate to edit page
      // For now, show member details
      showToast.info(`Edit functionality for ${member.FirstName} ${member.LastName} coming soon...`);
      
      // Future implementation:
      // - Open modal with member details
      // - Allow editing name, email, company, role
      // - Call API: PUT /user/update/:userId
      
    } catch (error) {
      console.error('Error preparing edit:', error);
      showToast.error('Failed to prepare edit');
    }
  };

  // Same filtering logic as Team.js
  const activeMembers = teamMembers.filter(m => m.isTeamActive !== false);
  const removedMembers = teamMembers.filter(m => m.isTeamActive === false);

  // Filter and search logic
  const filteredMembers = teamMembers.filter(member => {
    const matchesSearch = 
      member.FirstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.LastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.EmailAddress?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.CompanyName?.toLowerCase().includes(searchTerm.toLowerCase());

    const isActive = member.isTeamActive !== false;
    
    if (filter === 'active') return matchesSearch && isActive;
    if (filter === 'inactive') return matchesSearch && !isActive;
    return matchesSearch;
  });

  const getRoleIcon = (roleName) => {
    switch (roleName?.toLowerCase()) {
      case 'owner': return <FiStar className="tm-role-icon owner" />;
      case 'admin': case 'superadmin': return <FiShield className="tm-role-icon admin" />;
      case 'manager': return <FiUsers className="tm-role-icon manager" />;
      default: return <FiUser className="tm-role-icon member" />;
    }
  };

  if (loading) {
    return (
      <div className="team-members-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading team members...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="team-members-page">
      <div className="page-header">
        <div className="header-left">
          <FiUsers className="page-icon" />
          <div>
            <h1>Team Members</h1>
            <p>Manage your team members and their roles</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-section">
        <div className="stat-card">
          <div className="stat-icon">
            <FiUsers />
          </div>
          <div className="stat-info">
            <h3>Total Members</h3>
            <p>{teamMembers.length}</p>
          </div>
        </div>
        <div className="stat-card active">
          <div className="stat-icon">
            <FiUsers />
          </div>
          <div className="stat-info">
            <h3>Active Members</h3>
            <p>{activeMembers.length}</p>
          </div>
        </div>
        <div className="stat-card inactive">
          <div className="stat-icon">
            <FiUserMinus />
          </div>
          <div className="stat-info">
            <h3>Inactive Members</h3>
            <p>{removedMembers.length}</p>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="controls-section">
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search members by name, email, or company..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-tabs">
          <button
            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All Members ({teamMembers.length})
          </button>
          <button
            className={`filter-tab ${filter === 'active' ? 'active' : ''}`}
            onClick={() => setFilter('active')}
          >
            Active ({activeMembers.length})
          </button>
          <button
            className={`filter-tab ${filter === 'inactive' ? 'active' : ''}`}
            onClick={() => setFilter('inactive')}
          >
            Inactive ({removedMembers.length})
          </button>
        </div>
      </div>

             {/* Members Table */}
       <div className="members-section">
         {filteredMembers.length === 0 ? (
           <div className="empty-state">
             <FiUsers className="empty-icon" />
             <h3>No team members found</h3>
             <p>
               {searchTerm 
                 ? `No members match your search "${searchTerm}"`
                 : filter === 'active' 
                   ? 'No active team members found'
                   : filter === 'inactive'
                   ? 'No inactive team members found'
                   : 'No team members found'
               }
             </p>
           </div>
         ) : (
           <div className="tm-table-container">
             <table className="tm-members-table">
               <thead>
                 <tr>
                   <th>Member</th>
                   <th>Email</th>
                   <th>Company</th>
                   <th>Role</th>
                   <th>Status</th>
                   <th>Actions</th>
                 </tr>
               </thead>
               <tbody>
                 {filteredMembers.map(member => {
                   const isCurrentUser = member.UserId === userId;
                   const isOwner = member.Roles && member.Roles.some(role => role.roleName === 'Owner' && role.isActive);
                   const isActive = member.isTeamActive !== false;
                   const primaryRole = member.Roles && member.Roles.length > 0 ? member.Roles[0] : null;

                   return (
                     <tr key={member.UserId} className={`tm-table-row ${!isActive ? 'tm-inactive-row' : ''}`}>
                       <td className="tm-member-cell">
                         <div className="tm-member-info">
                           <div className="tm-member-avatar">
                             {getRoleIcon(primaryRole?.roleName)}
                           </div>
                           <div className="tm-member-details">
                             <div className="tm-member-name">
                               {member.FirstName} {member.LastName}
                               {isCurrentUser && (
                                 <span className="tm-member-you">(You)</span>
                               )}
                             </div>
                           </div>
                         </div>
                       </td>
                       <td className="tm-email-cell">
                         <div className="tm-email-info">
                           <FiMail className="tm-info-icon" />
                           {member.EmailAddress}
                         </div>
                       </td>
                       <td className="tm-company-cell">
                         {member.CompanyName ? (
                           <div className="tm-company-info">
                             <FiBriefcase className="tm-info-icon" />
                             {member.CompanyName}
                           </div>
                         ) : (
                           <span className="tm-no-company">-</span>
                         )}
                       </td>
                       <td className="tm-role-cell">
                         <span className={`tm-role-badge ${primaryRole?.roleName?.toLowerCase() || 'member'}`}>
                           {primaryRole?.roleName?.toUpperCase() || 'MEMBER'}
                         </span>
                       </td>
                       <td className="tm-status-cell">
                         <span className={`tm-status-badge ${isActive ? 'tm-active' : 'tm-inactive'}`}>
                           {isActive ? 'Active' : 'Inactive'}
                         </span>
                       </td>
                       <td className="tm-actions-cell">
                         {!isCurrentUser && !isOwner && (
                           <div className="tm-table-actions">
                             {/* Edit button - only if user has edit permission */}
                             {hasPermission('canEditTeamMembers') && (
                               <button
                                 className="tm-edit-btn"
                                 onClick={() => handleEditMember(member.UserId)}
                                 title="Edit member"
                               >
                                 <FiEdit2 />
                               </button>
                             )}
                             {/* Delete user permanently button - only if user has delete permission */}
                             {hasPermission('canDeleteTeamMembers') && (
                               <button
                                 className="tm-delete-btn"
                                 onClick={() => handleDeleteUser(member.UserId)}
                                 title="Delete user permanently"
                               >
                                 <FiTrash2 />
                               </button>
                             )}
                           </div>
                         )}
                       </td>
                     </tr>
                   );
                 })}
               </tbody>
             </table>
           </div>
         )}
       </div>
    </div>
  );
};

export default TeamMembers;